import z from 'zod';

import { CompanyOwnershipStructure, Gender } from '../api';
import { IPasswordRule } from '../lib/types/password';
import {
  LOWERCASE_REGEX,
  NUMBER_REGEX,
  POST_CODE_REGEX,
  SPECIAL_REGEX,
  UPPERCASE_REGEX,
} from './regex';

// TODO for translation file

export const TITLES_GENDER = {
  [Gender.Male]: 'Mr.',
  [Gender.Female]: 'Ms.',
  [Gender.Other]: 'Other',
};

export const REGISTRANT_SELF_EMPLOYED_POSITION = 'Self-employed';
export const MEMBER_SHAREHOLDING_DIRECTOR_POSITION = 'Shareholding_Director';

export enum ERegistrantRoles {
  SelfEmployed = 'SelfEmployed',
  CompanyEmployeeOrRepresentative = 'CompanyEmployeeOrRepresentative',
  // DirectorOrShareholder = 'DirectorOrShareholder',
}

export enum EMemberRoles {
  Director = 'Director',
  Shareholder = 'Shareholder',
  ShareholdingDirector = 'ShareholdingDirector',
  CorporateShareholder = 'CorporateShareholder',
}

export const NAMES_MEMBER_ROLES = {
  [EMemberRoles.Director]: 'Director',
  [EMemberRoles.Shareholder]: 'Shareholder',
  [EMemberRoles.ShareholdingDirector]: 'Shareholding Director',
  [EMemberRoles.CorporateShareholder]: 'Corporate Shareholder',
  [ERegistrantRoles.SelfEmployed]: 'Self-employed',
  [ERegistrantRoles.CompanyEmployeeOrRepresentative]: 'Company employee',
};

export const NAMES_REGISTRANT_ROLES = {
  [ERegistrantRoles.SelfEmployed]: 'Self-employed',
  // [ERegistrantRoles.DirectorOrShareholder]: 'Director/Shareholder',
  [ERegistrantRoles.CompanyEmployeeOrRepresentative]: 'Company employee',
};

export const NAMES_OWNERSHIP_STRUCTURE = {
  [CompanyOwnershipStructure.Private]: 'Private Company',
  [CompanyOwnershipStructure.Public]: 'Public Company',
};

export const ERRORS_TEXTS = {
  required: () => 'This field is required',
  maxLength: (maxLength = 255) =>
    `This field must be less than ${maxLength.toString()} characters`,
  minLength: (minLength = 1) =>
    `This field must be more than ${minLength.toString()} characters`,
};

export const RULES = {
  required: (message: string = ERRORS_TEXTS.required()) =>
    z.string().min(1, message),
  requiredObject: (
    fields: Record<string, z.ZodType> = {},
    message: string = ERRORS_TEXTS.required(),
  ) =>
    z
      .object(fields)
      .refine((value) => Boolean(value), message)
      .nullable(),
  maximumStringLength: (
    maxLength = 255,
    message: string = ERRORS_TEXTS.maxLength(maxLength),
  ) => z.string().max(maxLength, message),
  minimumStringLength: (
    minLength = 1,
    message: string = ERRORS_TEXTS.minLength(minLength),
  ) => z.string().min(minLength, message),

  postCode: (message = 'Invalid post code') =>
    z
      .string()
      .min(3, 'Post code must be at least 3 characters')
      .max(50, 'Post code must be less than 50 characters')
      .regex(POST_CODE_REGEX, message),
};

export const ANIMATION_VARIANTS = {
  fade: {
    initial: { opacity: 0.2 },
    animate: { opacity: 1 },
    exit: { opacity: 0.2 },
    transition: { duration: 0.4, ease: 'easeInOut' as const },
  },
  slide: {
    initial: { x: 10 },
    animate: { x: 0 },
    exit: { x: -10 },
  },
};

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

export const PASSWORD_VALIDATORS_RULES: IPasswordRule[] = [
  {
    label: 'Letters',
    checkValidRule: (value: string) => LOWERCASE_REGEX.test(value),
  },
  {
    label: 'Capital letter',
    checkValidRule: (value: string) => UPPERCASE_REGEX.test(value),
  },
  {
    label: 'Number',
    checkValidRule: (value: string) => NUMBER_REGEX.test(value),
  },

  {
    label: 'Special symbol',
    checkValidRule: (value: string) => SPECIAL_REGEX.test(value),
  },
  {
    label: `${PASSWORD_MIN_LENGTH.toString()} or more characters`,
    checkValidRule: (value: string) => value.length >= PASSWORD_MIN_LENGTH,
  },
];
